import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';

export type ModuleState = components['schemas']['ModuleStateResponse'];
export type ModuleHistoryEntry = components['schemas']['ModuleHistoryEntryResponse'];
export type RoleCatalogEntry = components['schemas']['RoleCatalogEntryResponse'];
type PlaceKind = 'tenants' | 'organizations';

export type ModulesState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly ModuleState[];
      readonly needsOrganization: boolean;
    }
  | { readonly status: 'error'; readonly code: unknown; readonly httpStatus: number | undefined };

/**
 * Moduli (FR-AD-7): every module and its state in the place. Activating and deactivating answer
 * with the module's new state, which replaces its row; nothing else in the list changes.
 */
export function useModules(placeKind: PlaceKind, placeId: string) {
  const [state, setState] = useState<ModulesState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/administration/{placeKind}/{placeId}/modules', {
        params: { path: { placeKind, placeId } },
      })
      .then(
        ({ data, error, response }) => {
          if (cancelled) {
            return;
          }
          setState(
            data !== undefined && response.ok
              ? { status: 'ready', items: data.items, needsOrganization: data.needsOrganization }
              : { status: 'error', code: error, httpStatus: response.status },
          );
        },
        () => {
          if (!cancelled) {
            setState({ status: 'error', code: null, httpStatus: undefined });
          }
        },
      );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  /** Puts a module's new state in its row, as the act's answer gives it. */
  const replace = useCallback((next: ModuleState) => {
    setState((current) =>
      current.status === 'ready'
        ? {
            ...current,
            items: current.items.map((item) => (item.module.id === next.module.id ? next : item)),
          }
        : current,
    );
  }, []);

  return { state, reload, replace };
}

/** «Attiva qui» or «Disattiva qui»: the write, and the module's new state or the problem. */
export async function writeModule(
  act: 'activation' | 'deactivation',
  placeKind: PlaceKind,
  placeId: string,
  moduleId: string,
) {
  const path =
    act === 'activation'
      ? ('/v1/administration/{placeKind}/{placeId}/modules/{moduleId}/activation' as const)
      : ('/v1/administration/{placeKind}/{placeId}/modules/{moduleId}/deactivation' as const);
  return apiClient.POST(path, { params: { path: { placeKind, placeId, moduleId } }, body: {} });
}

export type ModuleHistoryState =
  | { readonly status: 'loading'; readonly items: readonly ModuleHistoryEntry[] }
  | {
      readonly status: 'ready';
      readonly items: readonly ModuleHistoryEntry[];
      readonly hasMore: boolean;
      readonly loadingMore: boolean;
    }
  | { readonly status: 'error'; readonly items: readonly ModuleHistoryEntry[] };

/**
 * The history of the modules that reach the place (FR-AD-7): at an organization its own acts and
 * its tenant's, at a tenant its own. Newest first, a page at a time.
 */
export function useModuleHistory(placeKind: PlaceKind, placeId: string, generation: number) {
  const [state, setState] = useState<ModuleHistoryState>({ status: 'loading', items: [] });
  const [cursor, setCursor] = useState<string | null>(null);
  const asked = useRef(0);

  const read = useCallback(
    (after: string | null) =>
      apiClient.GET('/v1/administration/{placeKind}/{placeId}/modules/history', {
        params: { path: { placeKind, placeId }, query: after !== null ? { cursor: after } : {} },
      }),
    [placeKind, placeId],
  );

  useEffect(() => {
    const mine = ++asked.current;
    setState({ status: 'loading', items: [] });
    void read(null).then(
      ({ data, response }) => {
        if (asked.current !== mine) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', items: [] });
          return;
        }
        setCursor(data.nextCursor);
        setState({
          status: 'ready',
          items: data.items,
          hasMore: data.nextCursor !== null,
          loadingMore: false,
        });
      },
      () => {
        if (asked.current === mine) {
          setState({ status: 'error', items: [] });
        }
      },
    );
  }, [read, generation]);

  const more = useCallback(() => {
    if (state.status !== 'ready' || cursor === null || state.loadingMore) {
      return;
    }
    const before = state;
    const mine = asked.current;
    setState({ ...before, loadingMore: true });
    void read(cursor).then(
      ({ data, response }) => {
        if (asked.current !== mine) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', items: before.items });
          return;
        }
        setCursor(data.nextCursor);
        setState({
          status: 'ready',
          items: [...before.items, ...data.items],
          hasMore: data.nextCursor !== null,
          loadingMore: false,
        });
      },
      () => {
        if (asked.current === mine) {
          setState({ status: 'error', items: before.items });
        }
      },
    );
  }, [state, cursor, read]);

  return { state, more };
}

/** The place's role catalog (read only), for a module card's «Ruoli del modulo». */
export function useRoleCatalog(placeKind: PlaceKind, placeId: string) {
  const [roles, setRoles] = useState<readonly RoleCatalogEntry[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    void apiClient
      .GET('/v1/administration/{placeKind}/{placeId}/role-catalog', {
        params: { path: { placeKind, placeId } },
      })
      .then(
        ({ data, response }) => {
          if (!cancelled) {
            setRoles(data !== undefined && response.ok ? data.items : []);
          }
        },
        () => {
          if (!cancelled) {
            setRoles([]);
          }
        },
      );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId]);
  return roles;
}
